import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

export default function Setup({ onComplete }) {
  const [step, setStep] = useState(1);
  const [inviteUrl, setInviteUrl] = useState('');
  const [guilds, setGuilds] = useState([]);
  const [selectedGuild, setSelectedGuild] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const session = localStorage.getItem('session');
  const headers = { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' };

  // Step 1: Get bot invite URL
  useEffect(() => {
    if (step === 1) {
      fetch(`${API_BASE}/tenant/invite`, { headers })
        .then(r => r.json())
        .then(d => setInviteUrl(d.invite_url))
        .catch(() => {});
    }
  }, [step]);

  // Step 2: Get guilds from JWT (already fetched server-side)
  useEffect(() => {
    if (step === 2) {
      try {
        const part = session.split('.')[1];
        const padded = part + '='.repeat((4 - part.length % 4) % 4);
        const data = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
        const owned = data.user_guilds || [];
        setGuilds(owned);
        if (owned.length === 1) setSelectedGuild(owned[0]);
      } catch { setError('Session error — try logging in again'); }
    }
  }, [step]);

  const registerTenant = async () => {
    if (!selectedGuild) return;
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/tenant/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ guild_id: selectedGuild.id, guild_name: selectedGuild.name })
      });
      const data = await resp.json();
      if (resp.ok || resp.status === 409) {
        setStep(3);
        setTimeout(() => onComplete(selectedGuild.id), 2500);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (e) {
      setError('Network error');
    }
    setLoading(false);
  };

  return (
    <div className="setup-overlay">
      <div className="setup-card">
        <h2>🔮 Welcome to DNDLorewalkerService</h2>
        <p className="setup-subtitle">Let's get your campaign connected in 2 steps.</p>

        {/* Progress indicator */}
        <div className="setup-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>✓</div>
        </div>

        {step === 1 && (
          <div className="setup-step">
            <h3>🤖 Add the Bot to Your Server</h3>
            <p>Sanctum Steward needs to join your Discord server to run your campaign.</p>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '8px' }}>
              You must be the server owner or have Administrator permissions.
            </p>
            {inviteUrl ? (
              <a href={inviteUrl} target="_blank" rel="noreferrer" className="setup-btn">
                🤖 Invite Sanctum Steward
              </a>
            ) : <p>Loading invite link...</p>}
            <button className="setup-link" onClick={() => setStep(2)}>
              ✅ Done — I've added the bot
            </button>
            <button className="setup-link" style={{ opacity: 0.5 }} onClick={() => setStep(2)}>
              Already have the bot? Skip →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="setup-step">
            <h3>🏰 Link Your Server</h3>
            <p>Select the Discord server where you added the bot.</p>
            {error && <p className="setup-error">{error}</p>}
            <div className="guild-list">
              {guilds.length === 0 && !error && <p style={{ color: 'var(--text-dim)' }}>Loading your servers...</p>}
              {guilds.map(g => (
                <div
                  key={g.id}
                  className={`guild-item${selectedGuild?.id === g.id ? ' selected' : ''}`}
                  onClick={() => setSelectedGuild(g)}
                >
                  {g.icon ? (
                    <img src={`https://cdn.discordapp.com/icons/${g.id}/${g.icon}.png?size=32`} alt="" className="guild-icon" />
                  ) : (
                    <div className="guild-icon-placeholder">{g.name[0]}</div>
                  )}
                  <span>{g.name}</span>
                </div>
              ))}
            </div>
            <button className="setup-btn" onClick={registerTenant} disabled={!selectedGuild || loading}>
              {loading ? 'Linking...' : '🔗 Link This Server'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="setup-step" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
            <h3>You're All Set!</h3>
            <p>Your campaign is connected. Loading your dashboard...</p>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '12px' }}>
              Next: Type <code>!createchar</code> in Discord to create your first character.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
